import { calculateWorkOrderCosts } from '@calculations/cost/work-order';
import { calculateUnitPriceFromGrossMargin } from '@calculations/price/utilities/gross-margin';
import { calculateMarginsFromUnitPrice } from '@calculations/price/utilities/margins';
import {
  snapRoundDecimal,
  SnapRoundingOptionsCustom,
  snapRoundPerUnit,
} from '@calculations/round/decimal';
import { PriceStrategy } from '@snap-types/models/enums';

import { storeToCalculationSnapWorkOrder } from '../../transforms/store-to-calculation-snap-work-order';
import { transformWorkOrderCostsToStoreState } from '../../utilities/transform-work-order-costs';
import {
  OnQuantityChangeInput,
  OnQuantityChangeOutput,
  OnQuotePriceCreationInput,
  OnQuotePriceCreationOutput,
  OnUnitPriceChangeInput,
  OnUnitPriceChangeOutput,
  RecalculatePriceStrategy,
} from '../types';
import {
  OnBaseGrossMarginChangeInput,
  OnBaseGrossMarginChangeOutput,
} from '../types/base-gross-margin';

function onQuantityChange({
  state,
  quotePrice,
  quoteLine,
  quantity,
  priceStrategyConfigs,
}: OnQuantityChangeInput): OnQuantityChangeOutput {
  if (!quoteLine.snapWorkOrder) {
    return {
      status: 'failure',
      reason: 'No Snap Work Order Found',
    };
  }

  const priceStrategyConfig = priceStrategyConfigs.find(
    (config) => config.priceStrategyType === quotePrice.priceStrategy,
  );

  if (
    !priceStrategyConfig ||
    priceStrategyConfig.priceStrategyType !==
      PriceStrategy.PriceSegmentationWithTargetGrossMargin
  ) {
    return {
      status: 'failure',
      reason: 'No price strategy config found',
    };
  }

  const initialGrossMargin =
    quotePrice.grossMargin ?? priceStrategyConfig.targetGrossMargin;

  if (!quoteLine.priceAdjustment) {
    return {
      status: 'failure',
      reason: 'No Price Adjustment Found',
    };
  }

  const costs = calculateWorkOrderCosts({
    quantity,
    ...storeToCalculationSnapWorkOrder(quoteLine.snapWorkOrder),
  });

  // Calculate unit price based on target gross margin
  const baseUnitPrice = calculateUnitPriceFromGrossMargin({
    unitCost: costs.unitCost,
    grossMargin: initialGrossMargin,
  });

  // Calculate price adjustment amount from base unit price
  const priceAdjustmentAmount = snapRoundDecimal(
    baseUnitPrice.times(quoteLine.priceAdjustment),
  );

  // Calculate unit price based on price adjustment amount
  const unitPrice = snapRoundPerUnit(
    baseUnitPrice.plus(priceAdjustmentAmount),
    SnapRoundingOptionsCustom(state),
  );

  // Calculate gross margin from unit price
  const margins = calculateMarginsFromUnitPrice({
    unitCost: costs.unitCost,
    unitVariableCosts: costs.unitVariableCosts,
    unitPrice,
  });

  return {
    status: 'success',
    unitPrice,
    baseUnitPrice,
    priceAdjustmentAmount,
    ...margins,
    ...transformWorkOrderCostsToStoreState(costs),
  };
}

function onBaseGrossMarginChange({
  state,
  quotePrice,
  quoteLine,
  baseGrossMargin,
}: OnBaseGrossMarginChangeInput): OnBaseGrossMarginChangeOutput {
  if (!quotePrice.unitCost) {
    return {
      status: 'failure',
      reason: 'No unit cost found',
    };
  }

  if (!quoteLine.priceAdjustment) {
    return {
      status: 'failure',
      reason: 'No Price Adjustment Found',
    };
  }

  // Calculate unit price based on target gross margin
  const baseUnitPrice = calculateUnitPriceFromGrossMargin({
    unitCost: quotePrice.unitCost,
    grossMargin: baseGrossMargin,
  });

  // Calculate price adjustment amount from base unit price
  const priceAdjustmentAmount = snapRoundDecimal(
    baseUnitPrice.times(quoteLine.priceAdjustment),
  );

  // Calculate unit price based on price adjustment amount
  const unitPrice = snapRoundPerUnit(
    baseUnitPrice.plus(priceAdjustmentAmount),
    SnapRoundingOptionsCustom(state),
  );

  // Calculate gross margin from unit price
  const margins = calculateMarginsFromUnitPrice({
    unitCost: quotePrice.unitCost,
    unitVariableCosts: quotePrice.unitVariableCosts,
    unitPrice,
  });

  return {
    status: 'success',
    unitPrice,
    baseUnitPrice,
    priceAdjustmentAmount,
    ...margins,
  };
}

function onUnitPriceChange({
  quotePrice,
  unitPrice,
}: OnUnitPriceChangeInput): OnUnitPriceChangeOutput {
  if (!quotePrice.unitCost) {
    return {
      status: 'failure',
      reason: 'No unit cost found',
    };
  }

  const margins = calculateMarginsFromUnitPrice({
    unitCost: quotePrice.unitCost,
    unitVariableCosts: quotePrice.unitVariableCosts,
    unitPrice,
  });

  return {
    status: 'success',
    ...margins,
  };
}

function onQuotePriceCreation({
  state,
  quoteLine,
  quotePrice,
  priceStrategyConfigs,
}: OnQuotePriceCreationInput): OnQuotePriceCreationOutput {
  const { quantity } = quotePrice;

  if (!quoteLine.snapWorkOrder) {
    return {
      status: 'failure',
      reason: 'No Snap Work Order Found',
    };
  }

  const priceStrategyConfig = priceStrategyConfigs.find(
    (config) => config.priceStrategyType === quotePrice.priceStrategy,
  );

  if (
    !priceStrategyConfig ||
    priceStrategyConfig.priceStrategyType !== PriceStrategy.CAPPriceSegmentation
  ) {
    return {
      status: 'failure',
      reason: 'No price strategy config found',
    };
  }

  const targetGrossMargin =
    quotePrice.grossMargin ?? priceStrategyConfig.targetGrossMargin;

  if (!quoteLine.priceAdjustment) {
    return {
      status: 'failure',
      reason: 'No Price Adjustment Found',
    };
  }

  const costs = calculateWorkOrderCosts({
    quantity,
    ...storeToCalculationSnapWorkOrder(quoteLine.snapWorkOrder),
  });

  // Calculate unit price based on target gross margin
  const baseUnitPrice = calculateUnitPriceFromGrossMargin({
    unitCost: costs.unitCost,
    grossMargin: targetGrossMargin,
  });

  // Calculate price adjustment amount from base unit price
  const priceAdjustmentAmount = snapRoundDecimal(
    baseUnitPrice.times(quoteLine.priceAdjustment),
  );

  // Calculate unit price based on price adjustment amount
  const unitPrice = snapRoundPerUnit(
    baseUnitPrice.plus(priceAdjustmentAmount),
    SnapRoundingOptionsCustom(state),
  );

  // Calculate gross margin from unit price
  const margins = calculateMarginsFromUnitPrice({
    unitCost: costs.unitCost,
    unitVariableCosts: costs.unitVariableCosts,
    unitPrice,
  });

  return {
    status: 'success',
    quotePrice: {
      ...quotePrice,
      unitPrice,
      ...margins,
      ...transformWorkOrderCostsToStoreState(costs),
    },
  };
}

export const recalculatePriceSegmentationWithTargetGrossMargin = {
  onQuantityChange,
  onBaseGrossMarginChange,
  onUnitPriceChange,
  onContributionMarginChange: null,
  onGrossMarginChange: null,
  onQuotePriceCreation,
  onUnitCostChange: null,
} satisfies RecalculatePriceStrategy;
